import React, { useState } from "react";

import { useNavigate } from "react-router-dom";

import { Stack, Divider } from "@mui/material";

import TeamDeleteConfirmationModal from "@app/analytics/account/WorkspaceSettings/Teams/TeamDeleteConfirmationModal";
import ROUTES from "@app/common/routes";
import useWorkspace from "@app/common/workspaces/useWorkspace";
import { userIsTheOnlyTeamManager } from "@app/common/workspaces/workspaceUtils";

import Button from "@rui/Button";
import TooltipWrapper from "@rui/TooltipWrapper";

import LeaveConfirmationModal from "../../../components/LeaveConfirmationModal";
import SettingsTitle from "../../components/SettingsTitle";
import WorkspaceSettingsAccordion from "../components/WorkspaceSettingsAccordion";

const TeamActionsModule = ({ teamId }: { teamId: string }) => {
  const [confirmId, setConfirmId] = useState<"leave" | "remove" | null>(null);

  const { deleteTeam, removeUsersFromTeam, workspace, workspaceUser } =
    useWorkspace();

  const navigate = useNavigate();

  const teamUser = workspaceUser.teams?.find((team) => team.id === teamId);
  const userIsInTeam = Boolean(teamUser);
  const userCanRemoveSelfFromTeam =
    userIsInTeam &&
    !userIsTheOnlyTeamManager(workspaceUser.userId, workspace, teamId);

  const handleLeaveTeamConfirm = async () => {
    if (!workspaceUser.userId) {
      return;
    }

    setConfirmId(null);
    removeUsersFromTeam.mutate(
      {
        teamId: teamId!,
        userIds: [workspaceUser.userId],
      },
      {
        onSuccess: () => {
          navigate(ROUTES.WORKSPACE_SETTINGS_ROOT);
        },
      }
    );
  };

  const handleDeleteTeamConfirm = () => {
    setConfirmId(null);
    deleteTeam.mutate({ id: teamId });
  };

  return (
    <>
      <LeaveConfirmationModal
        loading={removeUsersFromTeam.isPending}
        open={confirmId === "leave"}
        onClose={() => setConfirmId(null)}
        onConfirm={handleLeaveTeamConfirm}
      />
      <TeamDeleteConfirmationModal
        open={confirmId === "remove"}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDeleteTeamConfirm}
      />
      <WorkspaceSettingsAccordion
        title="Team Actions"
        subtitle={
          userIsInTeam ? `Leave or delete this team` : "Delete this team"
        }
      >
        {userIsInTeam && (
          <Stack direction="column">
            <Stack
              direction="row"
              sx={{ minHeight: "42px", mb: 2 }}
              alignItems="center"
            >
              <SettingsTitle
                title="Leave Team"
                subtitle="Leaving this team means you will no longer have access to team-level reporting. There must be a manager appointed to leave the team."
                sx={{ width: "100%" }}
              />
              <TooltipWrapper
                show={!userCanRemoveSelfFromTeam}
                title="You are the only manager for this Team. There must be another manager appointed to leave the team."
              >
                <Button
                  loading={removeUsersFromTeam.isPending}
                  variant="text"
                  sx={{ minWidth: "160px", height: "42px" }}
                  onClick={() => setConfirmId("leave")}
                  disabled={!userCanRemoveSelfFromTeam}
                >
                  Leave Team
                </Button>
              </TooltipWrapper>
            </Stack>
            <Divider />
          </Stack>
        )}
        <Stack direction="column">
          <Stack direction="row" sx={{ minHeight: "42px" }} alignItems="center">
            <SettingsTitle
              title="Delete Team"
              subtitle="This action is permanent and cannot be undone. All team members will be notified."
              sx={{ width: "100%" }}
            />
            <Button
              loading={deleteTeam.isPending}
              variant="text"
              color="error"
              sx={{ minWidth: "160px", height: "42px" }}
              onClick={() => setConfirmId("remove")}
            >
              Delete Team
            </Button>
          </Stack>
        </Stack>
      </WorkspaceSettingsAccordion>
    </>
  );
};

export default TeamActionsModule;
